import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Calculator, 
  History, 
  TrendingUp, 
  LogOut, 
  Euro, 
  Calendar,
  Trash2,
  RefreshCw,
  ArrowUpRight,
  ChevronRight,
  Loader2,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  
  const [indices, setIndices] = useState({ ipc: 2.9, irav: 2.32 });
  const [currentRent, setCurrentRent] = useState('');
  const [contractType, setContractType] = useState('');
  const [calculations, setCalculations] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingIndices, setLoadingIndices] = useState(true);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchIndices = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/indices`);
      setIndices(response.data);
    } catch (error) {
      console.error('Error fetching indices:', error);
    } finally {
      setLoadingIndices(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/calculations`, { headers: authHeaders });
      setCalculations(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchIndices();
    fetchHistory();
  }, [fetchIndices, fetchHistory]);

  const handleCalculate = async () => {
    if (!currentRent || !contractType) {
      toast.error('Por favor, completa todos los campos');
      return;
    }

    const rent = parseFloat(currentRent);
    if (isNaN(rent) || rent <= 0) {
      toast.error('Introduce un importe de renta válido');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/calculations`,
        { current_rent: rent, contract_type: contractType },
        { headers: authHeaders }
      );
      setResult(response.data);
      toast.success('Cálculo guardado en tu historial');
      fetchHistory();
    } catch (error) {
      toast.error('Error al realizar el cálculo');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/calculations/${id}`, { headers: authHeaders });
      toast.success('Cálculo eliminado');
      fetchHistory();
    } catch (error) {
      toast.error('Error al eliminar el cálculo');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const comparisonData = result ? [
    { name: 'IPC', value: result.new_rent_ipc, color: '#3B82F6' },
    { name: 'IRAV', value: result.new_rent_irav, color: '#10B981' }
  ] : [];

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_mivau-clone/artifacts/ciik1x1e_logo.png" alt="JBR Inmobiliaria" className="h-9 w-auto" />
              <span className="text-xl font-bold text-white">JBRentUpdate</span>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-zinc-400 text-sm hidden sm:block">
                Hola, <span className="text-white font-medium">{user?.name}</span>
              </span>
              {user?.email === 'javierblanco@jbrinmobiliaria.com' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="text-amber-400 hover:text-amber-300"
                  data-testid="admin-button"
                >
                  <Shield className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-white"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Live Indices Ticker */}
        <div className="flex flex-wrap gap-4 mb-8 fade-in">
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-800">
            <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot"></span>
            <span className="text-zinc-400 text-sm">En vivo</span>
          </div>
          <div className="index-badge index-badge-ipc" data-testid="ipc-badge">
            <span className="text-zinc-400">IPC</span>
            {loadingIndices ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <span className="text-lg font-bold">{indices.ipc}%</span>
            )}
          </div>
          <div className="index-badge index-badge-irav" data-testid="irav-badge">
            <span className="text-zinc-400">IRAV</span>
            {loadingIndices ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <span className="text-lg font-bold">{indices.irav}%</span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchIndices}
            className="text-zinc-500 hover:text-white"
            data-testid="refresh-indices-button"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calculator Widget - Main Focus */}
          <Card className="lg:col-span-2 glass-card border-zinc-800" data-testid="calculator-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calculator className="h-5 w-5 text-emerald-400" />
                Calculadora de Actualización
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Introduce tu renta actual y selecciona el tipo de contrato
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Rent Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-300">Renta actual mensual</label>
                <div className="relative">
                  <Euro className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                  <Input
                    type="number"
                    placeholder="850.00"
                    value={currentRent}
                    onChange={(e) => setCurrentRent(e.target.value)}
                    className="pl-12 h-14 text-xl font-mono bg-black/30 border-zinc-700 text-white placeholder:text-zinc-600 focus:border-emerald-500/50"
                    data-testid="rent-input"
                  />
                </div>
              </div>

              {/* Contract Type Selection */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-zinc-300">Tipo de contrato</label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div
                    onClick={() => setContractType('pre_may_2023')}
                    className={`contract-card p-4 rounded-xl border cursor-pointer ${
                      contractType === 'pre_may_2023'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-700 bg-zinc-900/50 hover:border-zinc-600'
                    }`}
                    data-testid="contract-type-pre"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className="h-5 w-5 text-blue-400" />
                      <span className="font-semibold text-white">Antes del 26/05/2023</span>
                    </div>
                    <p className="text-sm text-zinc-400">Se aplicará el IPC ({indices.ipc}%)</p>
                  </div>
                  
                  <div
                    onClick={() => setContractType('post_may_2023')}
                    className={`contract-card p-4 rounded-xl border cursor-pointer ${
                      contractType === 'post_may_2023'
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-zinc-700 bg-zinc-900/50 hover:border-zinc-600'
                    }`}
                    data-testid="contract-type-post"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className="h-5 w-5 text-emerald-400" />
                      <span className="font-semibold text-white">Desde el 26/05/2023</span>
                    </div>
                    <p className="text-sm text-zinc-400">Se aplicará el IRAV ({indices.irav}%)</p>
                  </div>
                </div>
              </div>

              {/* Calculate Button */}
              <Button
                onClick={handleCalculate}
                disabled={loading || !currentRent || !contractType}
                className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold btn-primary-glow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="calculate-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Calculando...
                  </>
                ) : (
                  <>
                    Calcular Subida Máxima
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              {/* Result Display */}
              {result && (
                <div className="mt-6 p-6 rounded-xl bg-zinc-900/80 border border-zinc-700 fade-in" data-testid="result-card">
                  <div className="text-center mb-6">
                    <p className="text-zinc-400 text-sm mb-2">Nueva renta máxima aplicable</p>
                    <p className="result-value" data-testid="result-new-rent">
                      {result.new_rent_applied.toFixed(2)}€
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <ArrowUpRight className="h-4 w-4 text-amber-400" />
                      <span className="text-amber-400 font-mono">
                        +{result.increase_amount.toFixed(2)}€/mes
                      </span>
                      <span className="text-zinc-500">
                        ({result.applied_rate}%)
                      </span>
                    </div>
                  </div>
                  
                  {/* Comparison */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-zinc-700">
                    <div className="text-center">
                      <p className="text-xs text-zinc-500 mb-1">Con IPC ({result.ipc_rate}%)</p>
                      <p className="text-lg font-mono text-blue-400">{result.new_rent_ipc.toFixed(2)}€</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-zinc-500 mb-1">Con IRAV ({result.irav_rate}%)</p>
                      <p className="text-lg font-mono text-emerald-400">{result.new_rent_irav.toFixed(2)}€</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* History Panel */}
          <Card className="glass-card border-zinc-800" data-testid="history-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="h-5 w-5 text-emerald-400" />
                Historial
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Tus últimos cálculos
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 rounded-lg skeleton"></div>
                  ))}
                </div>
              ) : calculations.length === 0 ? (
                <div className="text-center py-8">
                  <History className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">Aún no hay cálculos</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {calculations.slice(0, 10).map((calc) => (
                    <div
                      key={calc.id}
                      className="history-item p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 group"
                      data-testid={`history-item-${calc.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-mono text-lg">
                            {calc.current_rent.toFixed(2)}€ → {calc.new_rent_applied.toFixed(2)}€
                          </p>
                          <p className="text-xs text-zinc-500 mt-1">
                            {calc.contract_type === 'pre_may_2023' ? 'IPC' : 'IRAV'} ({calc.applied_rate}%)
                          </p>
                          <p className="text-xs text-zinc-600 mt-1">
                            {format(new Date(calc.created_at), "d MMM yyyy, HH:mm", { locale: es })}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(calc.id)}
                          className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 transition-opacity"
                          data-testid={`delete-calc-${calc.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comparison Chart */}
          {result && (
            <Card className="lg:col-span-3 glass-card border-zinc-800 fade-in" data-testid="comparison-chart-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-400" />
                  Comparativa IPC vs IRAV
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Visualiza la diferencia entre ambos índices para tu renta
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={comparisonData} barSize={60}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                      <XAxis 
                        dataKey="name" 
                        stroke="#71717A"
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#71717A"
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => `${value}€`}
                        domain={[result.current_rent * 0.98, 'auto']}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'rgba(24, 24, 27, 0.95)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                        formatter={(value) => [`${value.toFixed(2)}€`, 'Nueva renta']}
                      />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                        {comparisonData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="mt-6 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <p className="text-sm text-zinc-400">
                    <span className="font-semibold text-white">Diferencia:</span>{' '}
                    <span className="font-mono text-emerald-400">
                      {Math.abs(result.new_rent_ipc - result.new_rent_irav).toFixed(2)}€/mes
                    </span>
                    {' '}a favor del {result.new_rent_irav < result.new_rent_ipc ? 'IRAV' : 'IPC'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
